import * as Y from 'yjs';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { buildCollaborationCredential, getAuthToken, listNodeDiscussions } from './apiClient';
import { buildDeleteInboxBranchCommand } from './ideaInbox';
import { waitForMapSync } from './branchMapWorkflow';

const canonical = value => Array.isArray(value) ? value.map(canonical)
  : value && typeof value === 'object'
    ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value;
const fingerprint = value => JSON.stringify(canonical(value));

export function planInboxTransfer(doc, nodeId, actorUserId) {
  const nodes = [...doc.getMap('nodes')].map(([id, node]) => ({ ...node, id }));
  const relationships = [...doc.getMap('relationships').values()];
  const command = buildDeleteInboxBranchCommand({ nodeId, nodes, relationships, actorUserId });
  if (command.nodeIds.length > 100) throw new Error('Move a branch with at most 100 ideas.');
  const entries = command.nodeIds.map(id => [id, doc.getMap('nodes').get(id)]);
  if (entries.some(([, node]) => node.isRoot)) throw new Error('The main idea cannot be moved between maps.');
  if (entries.some(([, node]) => node.attachment)) {
    throw new Error('Ideas with attachments cannot be moved between maps yet.');
  }
  return { command, entries, fingerprint: fingerprint({ entries, relationships: command.removedRelationships }) };
}

export function copyInboxTransfer(destination, plan) {
  const nodes = destination.getMap('nodes');
  const relationships = destination.getMap('relationships');
  // Preserve IDs so retrying after an uncertain acknowledgement cannot duplicate ideas.
  for (const [id, node] of plan.entries) {
    if (nodes.has(id) && fingerprint(nodes.get(id)) !== fingerprint(node)) {
      throw new Error('The destination already contains a different version of this idea. The original is kept.');
    }
    if ([...relationships.values()].some(edge => edge.childId === id
      && !plan.command.removedRelationships.some(original => fingerprint(original) === fingerprint(edge)))) {
      throw new Error('This idea has already been placed in the destination map. The original is kept.');
    }
  }
  destination.transact(() => {
    for (const [id, node] of plan.entries) if (!nodes.has(id)) nodes.set(id, node);
    for (const edge of plan.command.removedRelationships) {
      relationships.set(`${edge.parentId}:${edge.childId}`, edge);
    }
  }, 'move-inbox-between-maps');
}

export function removeTransferredInbox(source, destination, plan, actorUserId) {
  const current = planInboxTransfer(source, plan.command.nodeId, actorUserId);
  if (current.fingerprint !== plan.fingerprint) {
    throw new Error('The idea changed during the move. A copy reached the destination; the original is kept.');
  }
  const targetNodes = destination.getMap('nodes');
  const targetRelationships = destination.getMap('relationships');
  if (plan.entries.some(([id, node]) => fingerprint(targetNodes.get(id)) !== fingerprint(node))
    || plan.command.removedRelationships.some(edge => fingerprint(targetRelationships.get(`${edge.parentId}:${edge.childId}`)) !== fingerprint(edge))) {
    throw new Error('The destination idea changed during the move. The original is kept.');
  }
  source.transact(() => {
    for (const [id] of plan.entries) source.getMap('nodes').delete(id);
    for (const edge of plan.command.removedRelationships) {
      source.getMap('relationships').delete(`${edge.parentId}:${edge.childId}`);
    }
  }, 'move-inbox-between-maps');
}

function connectDestination(mapId, doc) {
  let provider;
  let scope = null;
  const ready = new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Could not connect to the destination map. Try again when online.')), 15000);
    const fail = message => { clearTimeout(timer); reject(new Error(message)); };
    provider = new HocuspocusProvider({
      url: process.env.REACT_APP_YJS_WS_URL || 'ws://localhost:4000/ws',
      name: mapId,
      document: doc,
      token: buildCollaborationCredential(getAuthToken(), null),
      onAuthenticated: event => {
        scope = event.scope;
        if (scope !== 'read-write') fail('You need edit access to the destination map.');
      },
      onAuthenticationFailed: () => fail('Could not authenticate with the destination map.'),
      onSynced: ({ state }) => {
        if (state && scope === 'read-write') { clearTimeout(timer); resolve(); }
      },
      onClose: () => { scope = null; fail('The destination connection was lost. The original is kept.'); },
    });
    provider.awareness?.setLocalState(null);
  });
  return { provider, ready, canWrite: () => scope === 'read-write' };
}

export async function moveInboxToMap({ source, sourceMapId, targetMapId, nodeId, actorUserId, sourceProvider, isSourceWritable }) {
  if (!actorUserId || sourceMapId === targetMapId || !isSourceWritable()) {
    throw new Error('Connect to your map before moving an idea.');
  }
  await waitForMapSync(sourceProvider);
  const plan = planInboxTransfer(source, nodeId, actorUserId);
  const discussions = await listNodeDiscussions(sourceMapId, plan.command.nodeIds);
  if (discussions.discussions?.length) {
    throw new Error('Ideas with discussions cannot be moved between maps yet.');
  }
  const destination = new Y.Doc();
  const connection = connectDestination(targetMapId, destination);
  try {
    await connection.ready;
    if (!isSourceWritable()) throw new Error('The original map connection changed. Try again when online.');
    if (planInboxTransfer(source, nodeId, actorUserId).fingerprint !== plan.fingerprint) {
      throw new Error('The idea changed while connecting. Try the move again.');
    }
    copyInboxTransfer(destination, plan);
    await waitForMapSync(connection.provider);
    if (!isSourceWritable() || !connection.canWrite()) {
      throw new Error('The connection changed during the move. The original is kept; check the destination before retrying.');
    }
    removeTransferredInbox(source, destination, plan, actorUserId);
    await waitForMapSync(sourceProvider);
  } finally {
    connection.provider.destroy();
    destination.destroy();
  }
}
